/**
 * Storefront v6 — the page.
 *
 * Reads the tenant through the existing public endpoints (useStorefrontData),
 * derives the theme from the brand (deriveTheme), infers the business model
 * and the section plan (sectionPlan), and renders the sections that have
 * something to show. Commerce is the EXISTING machinery: the storefront
 * gate, CartContext + /customers/checkout for plans, passes and catering,
 * the /order page for à-la-carte (the one money rail — sizes, extras, tax,
 * delivery and Stripe live there), the same dialogs, the same chat entry
 * points, the same live-order chip.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { X } from 'lucide-react';
import { useCart } from '../../../contexts/CartContext';
import useStorefrontGate from '../../../hooks/useStorefrontGate';
import useOpsyName from '../../../hooks/useOpsyName';
import { ensureStorefrontFont } from '../../../hooks/useStorefrontFonts';
import { loadActiveOrders, dropActiveOrder } from '../../../utils/activeOrders';
import StorefrontSEO from '../mealpass/StorefrontSEO';
import MealPassPurchaseModal from '../mealpass/MealPassPurchaseModal';
import AddonsOfferModal from '../mealpass/AddonsOfferModal';
import AddToCartModal from '../../storefront/AddToCartModal';
import CateringOrderModal from '../../storefront/CateringOrderModal';
import OpsyCustomerChat from '../OpsyCustomerChat';
import SupportChatWidget from '../../support/SupportChatWidget';
import './storefront-v6.css';
import { topLevelMealPlans } from '../../../utils/linkedMealPlans';
import StorefrontEntrance, { StorefrontLoadingSplash } from './StorefrontLoading';
import { deriveTheme, themeToCssVars } from './theme/deriveTheme';
import useStorefrontData, { isPreviewMode } from './data/useStorefrontData';
import WebsiteRenderer from './website/WebsiteRenderer';
import NativeWebsiteRenderer, { isNativeWebsite, nativeBrand, nativeText, nativeProductRows, nativeCollection, nativeHeroDestination, nativeSectionVisible, canvasChapter } from './website/NativeWebsiteRenderer';
import useWebsitePackage from './website/useWebsitePackage';
import { websiteCatalogue } from './website/catalogue';
import { inferBusinessModel, buildSectionPlan, heroCtasFor } from './plan/sectionPlan';
import OrderCutoff, { cutoffWindows, themeCutoffPlansHref } from './sections/OrderCutoff';
import { AnnouncementBar, Header, MobileMenuSheet, Footer } from './sections/Shell';
import { Hero, FulfilmentCard, CategoryRail, storeStatusFrom } from './sections/Hero';
import { PopularDishes, MenuBrowser, ProductSheet, groupByCategory, itemHasSizes, itemPrice } from './sections/Products';
import { MealPlans, HowItWorks, PromoBand, MealPasses, Catering } from './sections/Offers';
import { Reviews, About, InfoGrid, Gallery, Faq } from './sections/Story';
import { CartDrawer, CartBar } from './commerce/Cart';
import { StoryStage } from './story/StoryStage';
import { buildChapters, fulfilmentFacts, factLine, ownCopy, generatedPhotoUrls } from './story/chapters';
import { FactsStrip, buildFactCells, Closing } from './sections/Facts';

// Single-segment route words that are NOT store slugs (a bad redirect to
// /customers/cart must not scope the catalogue to a store named "cart").
export const RESERVED_SLUGS = new Set([
  'cart', 'checkout', 'login', 'dashboard', 'plans', 'meal-passes', 'reset-password', 'theme', 'success',
  'about', 'contact', 'catering', 'passes', 'menu', 'order', 'faq', 'reviews', 'gallery',
]);

const DEFAULT_STEPS = {
  meal_prep: [
    { title: 'Pick a plan', body: 'Choose the plan and the number of meals that fit your week.' },
    { title: 'Choose your days', body: 'Tell us when you eat with us. Change, pause or skip from your account.' },
    { title: 'We cook and deliver', body: 'Made fresh and delivered on your days.' },
  ],
  catering: [
    { title: 'Pick a package', body: 'Choose a spread and the number of guests.' },
    { title: 'Set the date', body: 'Tell us when and where; we confirm the details with you.' },
    { title: 'We cook and set up', body: 'Your food arrives ready for the table.' },
  ],
};

const planPhotoOf = (plans, plan) => {
  const full = plans.find((p) => p.id === plan?.id) || plan || {};
  return (Array.isArray(full.images) && full.images[0]) || full.image_url || null;
};

export default function Storefront({ orderMode = false, storeSlug: suppliedStoreSlug }) {
  const { storeSlug } = useParams();
  // A different store needs a fresh gate, catalogue and entrance, never the
  // previous store's ready state while its own requests are still in flight.
  return <StorefrontPage key={suppliedStoreSlug || storeSlug || ''} orderMode={orderMode} suppliedStoreSlug={suppliedStoreSlug} />;
}

function StorefrontPage({ orderMode, suppliedStoreSlug }) {
  const navigate = useNavigate();
  const { storeSlug: rawStoreSlug } = useParams();
  const storeSlug = suppliedStoreSlug || (RESERVED_SLUGS.has(rawStoreSlug) ? undefined : rawStoreSlug);

  const websiteState = useWebsitePackage({ storeSlug });
  const data = useStorefrontData({ storeSlug, strictCatalogue: !!websiteState.website, enabled: !isPreviewMode() || (!websiteState.loading && !websiteState.error) });
  const [entranceReady, setEntranceReady] = useState(false);
  const revealStorefront = useCallback(() => setEntranceReady(true), []);
  const [websiteFulfilment, setWebsiteFulfilment] = useState(false);
  const { brand: loadedBrand, settings, items, plans, tags, programs, orderable, catering, subdomain, isPreview, isAuthed, loading, error, tenantMeta, demoDataActive } = data;
  const brand = useMemo(() => nativeBrand(websiteState.website,loadedBrand),[websiteState.website,loadedBrand]);
  const gate = useStorefrontGate(subdomain, storeSlug);
  const theme = useMemo(() => deriveTheme(brand), [brand]);
  useEffect(() => {
    [theme.headingFont, theme.bodyFont, theme.scriptFont].forEach(ensureStorefrontFont);
  }, [theme.headingFont, theme.bodyFont, theme.scriptFont]);
  const { cartItems, removeFromCart, addCateringToCart } = useCart();
  const opsyName = useOpsyName(storeSlug);

  // ── chrome state ──────────────────────────────────────────────────────
  const headerRef = useRef(null);
  const heroRef = useRef(null);
  const searchRef = useRef(null);
  const [headerH, setHeaderH] = useState(72);
  const [pastHero, setPastHero] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  useEffect(() => {
    const onScroll = () => {
      const hero = heroRef.current;
      setPastHero(hero ? hero.getBoundingClientRect().bottom <= (headerRef.current?.offsetHeight || 72) : window.scrollY > 120);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [loading]);
  // A satellite page's header links land on a SECTION of this page (/customers#plans).
  // The sections do not exist until BOTH the catalogue has loaded AND the storefront
  // gate has opened - this page renders nothing at all while `gate.checking` - so the
  // landing waits for that one state and DEPENDS on it. Keying on `loading` alone was
  // a race: the gate's single request can settle after the catalogue's eight, and once
  // `loading` had already flipped, nothing re-ran the effect when the gate opened, so
  // the visitor was left at the top of the page (bugbot MED, PR #176 r2). The hash is
  // marked landed only AFTER a successful scroll, so an early run cannot consume the
  // attempt. Instant, once per hash; never a smooth-scroll hijack.
  const { hash: locationHash } = useLocation();
  let websiteFragment = '';
  try { websiteFragment = decodeURIComponent(locationHash.slice(1)); } catch (_e) { /* Ignore malformed fragments. */ }
  const landedHashRef = useRef('');
  const sectionsReady = !loading && !gate.checking && gate.allowed && !websiteState.loading && entranceReady;
  useEffect(() => {
    if (!sectionsReady || !locationHash || landedHashRef.current === locationHash) return;
    let id = '';
    try { id = decodeURIComponent(locationHash.slice(1)); } catch (_e) { id = locationHash.slice(1); }
    const el = id ? document.getElementById(id) : null;
    if (!el) return;
    landedHashRef.current = locationHash;
    el.scrollIntoView({ block: 'start' });
  }, [sectionsReady, locationHash]);

  // ── commerce state ────────────────────────────────────────────────────
  const [menuCart, setMenuCart] = useState({}); // { itemId: qty } — re-priced by /order
  const [sheetItem, setSheetItem] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [addonSubjectPlan, setAddonSubjectPlan] = useState(null);
  const [showAddonsOffer, setShowAddonsOffer] = useState(false);
  const [preAddCartCount, setPreAddCartCount] = useState(0);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [activeOrder, setActiveOrder] = useState(null);

  // ── derived facts ─────────────────────────────────────────────────────
  const currency = settings?.currency || programs[0]?.currency || 'CAD';
  const cateringPackages = catering.packages;
  // AN 86'd DISH IS NOT LISTED (founder, 2026-09-15: "it greys out sold out items - I
  // want them hidden"). `sellable` is the catalogue this page shows, and every surface
  // that renders or counts dishes reads it: the menu groups, the category rail, the
  // photographed strip, the stage tiles, the fact line.
  // OUT-OF-STOCK ONLY. An off-window dish is NOT hidden - it is listed with the reason
  // it cannot be ordered yet, because a lunch dish disappearing at 8pm reads as a menu
  // that is shrinking rather than one that opens again tomorrow.
  // Three readers stay on the FULL feed on purpose:
  //   * inferBusinessModel - what the merchant SELLS picks the page skeleton, so a
  //     sold-out evening must not re-order the page;
  //   * generatedPhotoUrls - provenance must see every photograph, or a hidden dish's
  //     generated picture goes undisclosed where the gallery reuses it;
  //   * the cart - a line already in the cart is marked, never silently dropped.
  const sellable = useMemo(() => items.filter((i) => !i.out_of_stock), [items]);
  const model = inferBusinessModel({ brand, plans, items, cateringPackages });
  const show = {
    passes: brand.mealpass_enabled !== false && programs.length > 0,
    plans: brand.mealplans_enabled !== false && plans.length > 0,
    menu: brand.menu_enabled !== false && sellable.length > 0,
    catering: brand.catering_enabled === true && cateringPackages.length > 0,
  };
  const orderableCount = orderable.sellableCount;
  const availableAddons = useMemo(() => plans.filter((p) => (p.is_meal_addon || p.is_delivery_addon) && Number(p.pricing?.tiers?.[0]?.price) > 0), [plans]);
  const sellablePlans = useMemo(() => topLevelMealPlans(plans), [plans]);
  const groups = useMemo(() => groupByCategory(sellable, []), [sellable]);
  const categories = useMemo(() => groups.map((g) => ({ key: g.key, label: g.label, count: g.items.length, photo: g.items.find((i) => i.image_url)?.image_url })), [groups]);
  const photographed = useMemo(() => sellable.filter((i) => i.image_url).slice(0, 8), [sellable]);
  const popularRows = nativeProductRows(websiteState.website,'popular',sellable,photographed);
  const popularMinimum = popularRows === photographed ? 3 : 1;
  // a gallery photo is generated when its own row says so, or when it IS one of the
  // catalogue's generated photographs - the launch engine builds the gallery straight
  // from the menu photos it just generated, and used to drop image_source doing it
  const catalogueAiUrls = generatedPhotoUrls(items);
  const galleryItems = Array.isArray(brand.gallery_items)
    ? brand.gallery_items.filter((g) => g && g.image_url)
      .map((g) => ({ ...g, generated: g.image_source === 'ai' || catalogueAiUrls.has(g.image_url) }))
    : [];
  const testimonials = Array.isArray(brand.testimonials) ? brand.testimonials : [];
  const faqs = Array.isArray(brand.faqs) ? brand.faqs : [];
  const heroPhoto = brand.hero_image_url || photographed[0]?.image_url || galleryItems[0]?.image_url || null;
  const status = storeStatusFrom(settings);
  const steps = (Array.isArray(brand.steps) && brand.steps.filter((s) => s && (s.title || s.body)).length ? brand.steps : DEFAULT_STEPS[model]) || [];
  const aboutHtml = brand.about_body_html || brand.story_body_html || brand.about_html || '';
  const marquee = (Array.isArray(brand.marquee_items) ? brand.marquee_items : []).filter((s) => String(s || '').trim());
  const pickupAvailable = !!settings?.pickup_address;
  const deliveryAvailable = brand.show_delivery_checker === true;
  // v7 — the cinematic stage: on unless the merchant switched it off; chapters come only from data
  const cinematic = brand.cinematic_enabled !== false;

  const facts = {
    announce: brand.show_marquee !== false && marquee.length > 0,
    hero: brand.show_hero !== false,
    fulfilment: (pickupAvailable || deliveryAvailable) && (orderableCount > 0 || show.plans),
    categories: show.menu && groups.length > 1,
    popular: brand.show_menu_showcase !== false && show.menu && popularRows.length >= popularMinimum,
    menu: show.menu,
    plans: show.plans && sellablePlans.length > 0,
    how: brand.show_how_it_works !== false && (show.plans || show.catering) && steps.length > 0,
    passes: show.passes,
    catering: show.catering,
    promo: !!String(brand.hero_offer_text || '').trim(),
    reviews: brand.show_testimonials !== false && testimonials.filter((t) => t && t.quote).length > 0,
    about: brand.show_story !== false && (!!aboutHtml || !!brand.story_image_url),
    info: !!(settings?.store_hours_enabled || brand.contact_address || settings?.pickup_address || Number(settings?.minimum_order_value) > 0),
    gallery: brand.show_gallery !== false && galleryItems.length > 0,
    faq: brand.show_faq !== false && faqs.filter((f) => f && f.q).length > 0,
    facts: true,
    closing: brand.show_punchline !== false && (orderableCount > 0 || show.plans || show.catering),
  };
  // AFTER `facts`, and reading it: the plans SECTION renders on facts.plans (which also
  // requires a sellable plan), so targeting show.plans could send the primary CTA to a
  // section the page dropped - the dead-end r1 fixed for `fulfilment` and left here.
  const ctas = heroCtasFor(model, brand, { orderable: orderableCount > 0, plans: facts.plans, passes: show.passes });
  const plan = useMemo(() => buildSectionPlan(model, facts, brand.storefront_sections), [model, JSON.stringify(facts), brand.storefront_sections]); // eslint-disable-line react-hooks/exhaustive-deps
  // the packed chapter may offer the address check only when the fulfilment card is on THIS page's plan
  const fulfilmentOn = plan.includes('fulfilment');
  // THE STAGE NEVER COUNTS OR SELLS A MENU THE MERCHANT SWITCHED OFF. Every v6 dish
  // section gates on show.menu and `menu_enabled` DEFAULTS FALSE, so without this a
  // pass-only storefront still announced its dish count and offered categories whose
  // section the page never renders.
  const menuItems = useMemo(() => (show.menu ? sellable : []), [show.menu, sellable]);
  const menuCategories = useMemo(() => (show.menu ? categories : []), [show.menu, categories]);
  const chapters = useMemo(() => buildChapters({
    model, brand, settings, plans: sellablePlans, items: menuItems, packages: cateringPackages, categories: menuCategories, currency,
    showPasses: show.passes, orderable: orderableCount, pickupAvailable, deliveryAvailable, fulfilmentOn, tenantName: tenantMeta?.name || '', ctas,
    aiPhotoUrls: catalogueAiUrls,
  }), [model, brand, settings, sellablePlans, menuItems, cateringPackages, menuCategories, currency, catalogueAiUrls, show.passes, orderableCount, pickupAvailable, deliveryAvailable, fulfilmentOn, tenantMeta, ctas]); // eslint-disable-line react-hooks/exhaustive-deps
  const stageOn = facts.hero && cinematic && chapters.length > 0;
  const factCells = buildFactCells({
    heroPoints: brand.hero_points,
    fulfilment: fulfilmentFacts({ settings, currency, pickupAvailable, deliveryAvailable }),
    countText: factLine({ model, plans: sellablePlans, items: menuItems, packages: cateringPackages, categories: menuCategories, currency }),
  });
  // the closing slab: the merchant's own punchline (the platform's pass copy is guarded), else the brand name
  const closingOwn = ownCopy(brand, 'punchline_headline', { passes: show.passes });
  const closingCopy = {
    headline: closingOwn || brand.brand_name || tenantMeta?.name || '',
    emphasis: closingOwn ? (ownCopy(brand, 'punchline_headline_italic', { passes: show.passes }) || '') : 'Ready when you are.',
    body: closingOwn ? ownCopy(brand, 'punchline_body', { passes: show.passes }) : null,
    cta: ownCopy(brand, 'punchline_cta', { passes: show.passes }) || ctas.primary,
  };
  const skip = facts.plans ? { label: 'Skip to plans', target: 'plans' } : show.catering && model === 'catering' ? { label: 'Skip to packages', target: 'catering' } : show.menu ? { label: 'Skip to menu', target: 'menu' } : null;

  // ── actions (the existing rails) ──────────────────────────────────────
  const tenantQs = () => {
    const p = new URLSearchParams(window.location.search);
    const keep = new URLSearchParams();
    for (const k of ['tenant', 'subdomain']) { const v = p.get(k); if (v) keep.set(k, v); }
    return keep.toString();
  };
  const checkoutDest = useCallback(() => {
    const base = storeSlug ? `/customers/${storeSlug}/checkout` : '/customers/checkout';
    const qs = tenantQs();
    return qs ? `${base}?${qs}` : base;
  }, [storeSlug]);
  const goOrderOnline = useCallback((handoffCart = null, itemCheckout = false) => {
    const parts = [];
    if (subdomain) parts.push(`tenant=${encodeURIComponent(subdomain)}`);
    if (storeSlug) parts.push(`store_slug=${encodeURIComponent(storeSlug)}`);
    parts.push('src=online');
    if (itemCheckout || (handoffCart && Object.keys(handoffCart).length > 0)) parts.push('checkout=items');
    if (handoffCart && Object.keys(handoffCart).length > 0) {
      try { sessionStorage.setItem('plateos_storefront_cart', JSON.stringify(handoffCart)); } catch (_e) { /* private mode */ }
    }
    navigate(`/order?${parts.join('&')}`);
  }, [navigate, subdomain, storeSlug]);
  const openSizePickerOnOrderPage = useCallback((item) => {
    try { sessionStorage.setItem('plateos_storefront_pick_size', item.id); } catch (_e) { /* private mode */ }
    goOrderOnline(null, true);
  }, [goOrderOnline]);
  const goToPage = (page) => {
    const base = storeSlug ? `/customers/${storeSlug}/${page}` : `/customers/${page}`;
    const qs = tenantQs();
    navigate(qs ? `${base}?${qs}` : base);
  };
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const bumpMenuCart = (item, delta) => setMenuCart((prev) => {
    const q = Math.max(0, (prev[item.id] || 0) + delta);
    const next = { ...prev };
    if (q) next[item.id] = q; else delete next[item.id];
    return next;
  });
  const addItem = (item) => {
    // A dated à-la-carte menu uses the meal picker so availability, options and
    // minimums are checked for the delivery week, not the immediate POS cart.
    const datedMenu = sellablePlans.find(p => p.storefront_selling?.mode === 'meal_pack'
      && p.storefront_selling?.pricing_mode === 'selected_items'
      && p.storefront_selling?.one_time_enabled !== false
      && p.meal_items?.some(meal => meal.item_id === item.id));
    if (datedMenu) { setSheetItem(null); setPreAddCartCount(cartItems.length); setSelectedPlan(datedMenu); return; }
    return itemHasSizes(item) ? openSizePickerOnOrderPage(item) : bumpMenuCart(item, 1);
  };
  const cardProps = (item) => ({
    currency,
    qty: menuCart[item.id] || 0,
    canOrder: orderable.ids.has(item.id),
    reason: orderable.reasons[item.id],
    onAdd: addItem,
    onRemove: (it) => bumpMenuCart(it, -1),
    onOpen: setSheetItem,
  });
  // THE CART RESOLVES AGAINST THE FULL FEED, NOT `sellable`. A dish that sells out
  // while it sits in someone's cart must be SAID, not deleted behind their back: drop it
  // here and the line leaves the drawer while its id still travels to /order in the
  // handoff, so the quantity is lost with no message. It stays, marked, removable - and
  // it is excluded from the estimate and from the handoff, which carry only real money.
  const menuLines = useMemo(() => Object.entries(menuCart)
    .map(([id, qty]) => { const item = items.find((i) => i.id === id); return { item, qty, soldOut: !!(item && item.out_of_stock) }; })
    .filter((l) => l.item), [menuCart, items]);
  const menuQty = menuLines.reduce((s, l) => s + l.qty, 0);
  const cartCount = cartItems.length + menuQty;
  const cartEstimate = cartItems.reduce((s, it) => s + (Number(it.price) || 0), 0)
    + menuLines.filter((l) => !l.soldOut).reduce((s, l) => s + itemPrice(l.item) * l.qty, 0);
  const handoffCart = useMemo(() => Object.fromEntries(menuLines.filter((l) => !l.soldOut).map((l) => [l.item.id, l.qty])), [menuLines]);

  const orderLabel = settings?.store_hours_enabled && settings?.is_open === false
    ? (settings?.next_open?.time ? `Schedule ahead, opens ${settings.next_open.time}` : 'Schedule an order')
    : 'Order now';
  const loginDest = storeSlug ? `/customers/${storeSlug}/login` : '/customers/login';
  const navItems = [
    { key: 'directory', label: 'Menu Directory', onClick: () => goToPage('menu') },
    show.menu && nativeSectionVisible(websiteState.website,'menu') && { key: 'menu', label: 'Menu', onClick: () => scrollTo('menu') },
    facts.plans && nativeSectionVisible(websiteState.website,'plans') && { key: 'plans', label: 'Plans', onClick: () => scrollTo('plans') },
    show.passes && nativeSectionVisible(websiteState.website,'passes') && { key: 'passes', label: 'Passes', onClick: () => scrollTo('passes') },
    show.catering && nativeSectionVisible(websiteState.website,'catering') && { key: 'catering', label: 'Catering', onClick: () => scrollTo('catering') },
    { key: 'about', label: 'About', onClick: () => goToPage('about') },
    { key: 'contact', label: 'Contact', onClick: () => goToPage('contact') },
    { key: 'account', label: isAuthed ? 'My dashboard' : 'Sign in', testid: isAuthed ? 'mealpass-store-back-button' : 'mealpass-store-signin-button', onClick: () => navigate(isAuthed ? (storeSlug ? `/customers/${storeSlug}/dashboard` : '/customers/dashboard') : loginDest) },
    orderableCount > 0 && { key: 'order', label: orderLabel, mobileLabel: settings?.store_hours_enabled && settings?.is_open === false ? 'Schedule' : 'Order', shortLabel: settings?.store_hours_enabled && settings?.is_open === false ? 'Schedule ahead' : 'Order now', primary: true, onClick: () => goOrderOnline(handoffCart) },
  ].filter(Boolean);
  const heroAction = (target) => {
    if (isNativeWebsite(websiteState.website) && target !== 'order') {
      if (target === 'home') return window.scrollTo({top:0,behavior:'smooth'});
      if (document.getElementById(target)) return scrollTo(target);
    }
    if (target === 'order') return goOrderOnline(handoffCart);
    if (target === 'plans') return scrollTo('plans');
    if (target === 'catering') return scrollTo('catering');
    if (target === 'how') return scrollTo(facts.plans ? 'plans' : 'how');
    if (target === 'fulfilment') return scrollTo('fulfilment');
    return scrollTo('menu');
  };
  // the stage's chapter-3 rows: a plan opens its dialog, a category scrolls to it, a package opens catering
  const storyRow = (row) => {
    if (row.kind === 'plan') { const p = sellablePlans.find((x) => x.id === row.id); if (p) { setPreAddCartCount(cartItems.length); setSelectedPlan(p); } return; }
    if (row.kind === 'package') { const pkg = cateringPackages.find((x) => x.id === row.id); if (pkg) setSelectedPackage(pkg); return; }
    scrollTo(document.getElementById(`cat-${row.id}`) ? `cat-${row.id}` : 'menu');
  };
  const stageStatus = status ? (status.is_open ? { tone: 'open', text: 'Open now' } : { tone: 'closed', text: status.opens_time ? `Closed · opens ${status.opens_time}` : 'Closed right now' }) : null;

  useEffect(() => {
    if (!websiteState.website) return undefined;
    const refresh = () => { if (document.visibilityState === 'visible') data.refetch(); };
    const timer = setInterval(refresh, 60000);
    window.addEventListener('focus', refresh);
    return () => { clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, [websiteState.website, data.refetch]);

  const websiteAction = (action, product) => {
    if (isPreview) return;
    if (action === 'commerce.configure' && product?.purchasable) {
      const kind = product.ref.split(':')[0];
      if (kind === 'meal_plan') { setPreAddCartCount(cartItems.length); setSelectedPlan(product.native); }
      if (kind === 'meal_pass') { setPreAddCartCount(cartItems.length); setSelectedProgram(product.native); }
      if (kind === 'meal_item') setSheetItem(product.native);
      if (kind === 'catering') setSelectedPackage(product.native);
    } else if (action === 'commerce.cart.open') setCartOpen(true);
    else if (action === 'commerce.checkout') setCartOpen(true);
    else if (action === 'customer.signIn') goToPage('login');
    else if (action === 'customer.portal') goToPage('dashboard');
    else if (action === 'catalogue.directory') goToPage('menu');
    else if (action === 'fulfilment') setWebsiteFulfilment(true);
  };

  // ── live order chip (the /order page writes the entry; we verify it) ──
  useEffect(() => {
    let cancelled = false;
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const tick = async () => {
      const entries = loadActiveOrders();
      if (!Array.isArray(entries) || !entries.length) { if (!cancelled) setActiveOrder(null); return; }
      const entry = entries[entries.length - 1];
      if (!entry?.token) return;
      try {
        const r = await fetch(`${base}/api/dine-in/orders/track?token=${encodeURIComponent(entry.token)}`);
        if (!r.ok) { dropActiveOrder(undefined, entry.order_id || entry.id); if (!cancelled) setActiveOrder(null); return; }
        const live = await r.json();
        if (live.status === 'cancelled' || live.prep_state === 'completed') { dropActiveOrder(undefined, entry.order_id || entry.id); if (!cancelled) setActiveOrder(null); return; }
        if (!cancelled) setActiveOrder({ entry, live });
      } catch (_e) { /* keep the last known state */ }
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // ── gate (fail closed; the hook redirects) ────────────────────────────
  if (gate.checking || !gate.allowed) {
    return <div data-testid="s6-gate"><StorefrontLoadingSplash /></div>;
  }

  const editable = (key,value) => nativeText(websiteState.website,key,value);
  const canvasChapters = chapters.map((chapter) => ({...chapter,
    title:editable(`story_chapters_${chapter.id}_headline`,chapter.title),
    emphasis:editable(`story_chapters_${chapter.id}_italic`,chapter.emphasis),
    sub:editable(`story_chapters_${chapter.id}_sub`,chapter.sub),
    eyebrow:editable(`story_chapters_${chapter.id}_eyebrow`,chapter.eyebrow),
  }));
  const copy = (eyebrow, title, emphasis, sub) => ({ eyebrow: editable(eyebrow,brand[eyebrow]), title: editable(title,brand[title]), emphasis: editable(emphasis,brand[emphasis]), sub: sub ? editable(sub,brand[sub]) : undefined });
  const headerItems = navItems;
  const hasOrderingCutoff = cutoffWindows(sellablePlans).length > 0;
  const overlay = facts.hero && !stageOn && !!heroPhoto && !pastHero;

  const cutoff = <OrderCutoff browsePlansHref={themeCutoffPlansHref(websiteState.website, `${storeSlug ? `/customers/${storeSlug}` : '/customers'}${tenantQs() ? `?${tenantQs()}` : ''}`)} plans={sellablePlans} onRefresh={data.refetch} onChoose={id => setSelectedPlan(sellablePlans.find(p => p.id === id))} />;
  const sections = {
    announce: () => <AnnouncementBar key="announce" items={marquee} />,
    header: () => (<React.Fragment key="header-shell">
      <Header
        key="header"
        headerRef={(el) => { headerRef.current = el; if (el && el.offsetHeight) setHeaderH(el.offsetHeight); }}
        brand={brand} theme={theme} items={headerItems} overlay={!orderMode && overlay && !hasOrderingCutoff} cartCount={cartCount}
        onCart={() => setCartOpen(true)} onSearch={show.menu ? () => { scrollTo('menu'); setTimeout(() => searchRef.current?.focus(), 350); } : null}
        onAccount={() => navigate(isAuthed ? (storeSlug ? `/customers/${storeSlug}/dashboard` : '/customers/dashboard') : loginDest)}
        accountLabel={isAuthed ? 'My dashboard' : 'Sign in'} onMenu={() => setMenuOpen(true)} menuOpen={menuOpen}
        onHome={() => orderMode ? goToPage('') : window.scrollTo({ top: 0, behavior: 'smooth' })}
      />{cutoff}</React.Fragment>
    ),
    hero: () => (stageOn ? (
      <StoryStage
        editorChapter={canvasChapter(websiteState.website)}
        key="hero" wrapRef={heroRef} chapters={canvasChapters} headerH={headerH} brandName={brand.brand_name || tenantMeta?.name || ''}
        status={stageStatus} skipLabel={skip ? skip.label : null} skipTarget={skip ? skip.target : null}
        actions={{ cta: (target) => heroAction(nativeHeroDestination(websiteState.website,target)), secondary: heroAction, row: storyRow, skip: (t) => scrollTo(t) }}
      />
    ) : (
      <Hero
        key="hero" heroRef={heroRef} brand={brand} theme={theme} photo={heroPhoto} headerH={facts.hero && heroPhoto && !hasOrderingCutoff ? headerH : 0}
        title={editable('hero_title',brand.hero_title || brand.brand_name || tenantMeta?.name || '')} emphasis={editable('hero_italic',brand.hero_italic || '')} subtitle={editable('hero_subtitle',brand.hero_subtitle || brand.tagline || '')}
        ctas={ctas} status={status}
        onPrimary={() => heroAction(nativeHeroDestination(websiteState.website,ctas.primaryTarget))} onSecondary={() => heroAction(ctas.secondaryTarget)}
      />
    )),
    facts: () => <FactsStrip key="facts" cells={factCells} />,
    closing: () => <Closing key="closing" {...closingCopy} onCta={() => heroAction(ctas.primaryTarget)} />,
    fulfilment: () => (
      <FulfilmentCard
        key="fulfilment" overlap={!stageOn} brand={brand} settings={settings} subdomain={subdomain} storeSlug={storeSlug} isPreview={isPreview} currency={currency}
        pickupAvailable={pickupAvailable} deliveryAvailable={deliveryAvailable}
        onOrder={orderableCount > 0 ? () => goOrderOnline(handoffCart) : () => scrollTo('plans')}
      />
    ),
    categories: () => nativeCollection(websiteState.website,'menu')?.changedKind ? null : <CategoryRail key="categories" categories={categories} active={activeCategory} top={headerH} onPick={(k) => { setActiveCategory(k); scrollTo(`cat-${k}`); }} />,
    popular: () => <PopularDishes key="popular" items={popularRows} minimumItems={popularMinimum} copy={copy('showcase_eyebrow', 'showcase_headline', 'showcase_headline_italic')} theme={theme} onSeeAll={() => scrollTo('menu')} cardProps={cardProps} />,
    menu: () => <MenuBrowser key="menu" groups={isNativeWebsite(websiteState.website) ? groupByCategory(nativeProductRows(websiteState.website,'menu',sellable),[]) : groups} currency={currency} loading={loading} searchRef={searchRef} cardProps={cardProps} theme={theme} copy={{ title: 'Our', emphasis: 'menu' }} onOrder={() => goOrderOnline(handoffCart)} orderLabel={orderLabel} orderableCount={orderableCount} />,
    plans: () => (
      <MealPlans
        key="plans" plans={nativeProductRows(websiteState.website,'plans',sellablePlans)} tags={tags} currency={currency} theme={theme} featuredId={brand.featured_plan_id || null}
        copy={copy('plans_eyebrow', 'plans_headline', 'plans_headline_italic', 'plans_subheadline')}
        onChoose={(p) => { setPreAddCartCount(cartItems.length); setSelectedPlan(p); }}
        howItWorks={facts.how && model === 'meal_prep' ? <HowItWorks embedded steps={steps} copy={{ eyebrow: brand.steps_eyebrow || 'How it works' }} /> : null}
      />
    ),
    how: () => (facts.plans && model === 'meal_prep' ? null : <HowItWorks key="how" steps={steps} copy={{ eyebrow: brand.steps_eyebrow || 'How it works' }} />),
    passes: () => <MealPasses key="passes" programs={nativeProductRows(websiteState.website,'passes',programs)} theme={theme} copy={copy('programs_eyebrow', 'programs_headline', 'programs_headline_italic', 'programs_subheadline')} onBuy={(p) => { setPreAddCartCount(cartItems.length); setSelectedProgram(p); }} />,
    catering: () => <Catering key="catering" packages={nativeProductRows(websiteState.website,'catering',cateringPackages)} availability={catering.availability} dishImages={catering.dishImages} currency={currency} theme={theme} copy={copy('catering_eyebrow', 'catering_headline', 'catering_headline_italic', 'catering_subheadline')} onOrder={setSelectedPackage} />,
    promo: () => <PromoBand key="promo" text={brand.hero_offer_text} cta={ctas.primary} onCta={() => heroAction(ctas.primaryTarget)} photo={photographed[1]?.image_url || null} />,
    reviews: () => <Reviews key="reviews" testimonials={testimonials} theme={theme} copy={copy('testimonials_eyebrow', 'testimonials_headline', 'testimonials_headline_italic')} />,
    about: () => <About key="about" brand={brand} theme={theme} copy={copy('story_eyebrow', 'story_headline', 'story_headline_italic')} />,
    info: () => <InfoGrid key="info" brand={brand} settings={settings} currency={currency} />,
    gallery: () => <Gallery key="gallery" items={galleryItems} theme={theme} copy={copy('gallery_eyebrow', 'gallery_headline', 'gallery_headline_italic')} onOpen={setLightbox} />,
    faq: () => <Faq key="faq" faqs={faqs} theme={theme} copy={copy('faqs_eyebrow', 'faqs_headline', 'faqs_headline_italic')} />,
    footer: () => <Footer key="footer" brand={brand} settings={settings} items={headerItems} poweredBy={!!settings?.powered_by_plateos} />,
  };

  return (
    <StorefrontEntrance ready={!loading && !websiteState.loading} error={error || websiteState.error} onReady={revealStorefront}>
    <div className="s6-root min-h-screen" style={themeToCssVars(theme)} data-testid="s6-storefront" data-model={model}>
      <StorefrontSEO brand={brand} subdomain={subdomain} pageType="home" programs={programs} mealPlans={plans} cateringPackages={cateringPackages} faqs={faqs} settings={settings} isConceptPreview={tenantMeta?.is_concept_preview === true} />

      {isPreview && demoDataActive && (
        <div data-testid="storefront-preview-demo-badge" className="fixed top-24 left-4 z-50 pointer-events-none select-none rounded-full bg-black/60 text-white/90 backdrop-blur-sm px-3 py-1.5 text-[11px] leading-none shadow-lg">
          Preview: sample data shown where your catalogue is empty
        </div>
      )}

      {!loading && !error && !websiteState.error && websiteState.website && !isNativeWebsite(websiteState.website) && <>{brand.show_marquee !== false && <AnnouncementBar items={marquee} />}{cutoff}<div className="s6-directory-entry"><button type="button" onClick={() => goToPage('menu')}>Menu Directory · Ingredients, nutrition & meal details →</button></div></>}
      {!error && !websiteState.error && !websiteState.loading && (websiteState.website
        ? isNativeWebsite(websiteState.website) ? <NativeWebsiteRenderer website={websiteState.website} sections={sections} plan={orderMode ? plan.filter(id => ['announce', 'header', 'plans', 'menu', 'passes', 'catering', 'footer'].includes(id)) : plan} order={buildSectionPlan(model,Object.fromEntries(Object.keys(facts).map((k) => [k,true])),brand.storefront_sections)} catalogue={websiteCatalogue({ plans, items, programs, catering, orderable, currency })} onAction={websiteAction} /> : <WebsiteRenderer initialPage={orderMode ? 'plans' : websiteFragment} initialSection={websiteFragment} website={websiteState.website} catalogue={websiteCatalogue({ plans, items, programs, catering, orderable, currency })} onAction={websiteAction} disabled={loading || isPreview} />
        : (orderMode ? plan.filter(id => ['announce', 'header', 'plans', 'menu', 'passes', 'catering', 'footer'].includes(id)) : plan).map((id) => (sections[id] ? sections[id]() : null)))}
      {websiteFulfilment && <div role="dialog" aria-modal="true" aria-label="Delivery and pickup" className="fixed inset-0 z-[70] bg-white overflow-auto p-6"><button type="button" className="s6-btn" onClick={() => setWebsiteFulfilment(false)}>Close</button>{sections.fulfilment()}</div>}

      <MobileMenuSheet open={menuOpen} onClose={() => setMenuOpen(false)} brand={brand} items={headerItems} />

      {activeOrder && (
        <button
          type="button"
          onClick={() => {
            const e = activeOrder.entry;
            const parts = [`tenant=${encodeURIComponent(subdomain)}`];
            if (e.store_slug) parts.push(`store_slug=${encodeURIComponent(e.store_slug)}`);
            parts.push(`track=${encodeURIComponent(e.token)}`);
            navigate(`/order?${parts.join('&')}`);
          }}
          className="fixed inset-x-4 md:inset-x-auto md:right-6 bottom-20 md:bottom-6 z-[61] mx-auto max-w-md s6-card px-4 py-3 text-left flex items-center gap-3"
          data-testid="mealpass-store-active-order-chip"
        >
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: 'var(--s6-open)' }} aria-hidden="true" />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold truncate">Order {activeOrder.live?.order_number ? `#${activeOrder.live.order_number}` : ''} · {activeOrder.live?.prep_state || activeOrder.live?.status || 'placed'}</span>
            <span className="block text-xs" style={{ color: 'var(--s6-ink-muted)' }}>Tap to track</span>
          </span>
        </button>
      )}

      <ProductSheet onDetails={item => { setSheetItem(null); goToPage(`menu/${encodeURIComponent(item.id)}`); }} item={sheetItem} currency={currency} qty={sheetItem ? (menuCart[sheetItem.id] || 0) : 0} canOrder={sheetItem ? orderable.ids.has(sheetItem.id) : false} reason={sheetItem ? orderable.reasons[sheetItem.id] : null} onAdd={addItem} onRemove={(it) => bumpMenuCart(it, -1)} onClose={() => setSheetItem(null)} onChooseSize={openSizePickerOnOrderPage} />

      <CartDrawer
        open={cartOpen} onClose={() => setCartOpen(false)} cartItems={cartItems} onRemoveCartItem={removeFromCart}
        menuLines={menuLines} onMenuQty={bumpMenuCart} currency={currency} settings={settings} subdomain={subdomain}
        onCheckout={() => { setCartOpen(false); navigate(checkoutDest()); }}
        onOrderDishes={() => { setCartOpen(false); goOrderOnline(handoffCart); }}
      />
      {!cartOpen && !sheetItem && <CartBar count={cartCount} total={cartEstimate} currency={currency} onOpen={() => setCartOpen(true)} />}

      {lightbox !== null && galleryItems[lightbox] && (
        <div className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4" role="dialog" aria-modal="true" onClick={() => setLightbox(null)} data-testid="mealpass-store-gallery-lightbox">
          <button type="button" onClick={() => setLightbox(null)} aria-label="Close photo" className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center"><X className="h-5 w-5" /></button>
          <figure className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={galleryItems[lightbox].image_url} alt={galleryItems[lightbox].caption || ''} className="w-full max-h-[80vh] object-contain rounded-xl" />
            {galleryItems[lightbox].generated && <span className="mt-3 block text-center text-white/70 text-xs" data-testid="storefront-ai-image-tag">AI generated image</span>}
            {galleryItems[lightbox].caption && <figcaption className="mt-3 text-center text-white/80 text-sm">{galleryItems[lightbox].caption}</figcaption>}
          </figure>
        </div>
      )}

      {selectedProgram && (
        <MealPassPurchaseModal
          program={selectedProgram} open={!!selectedProgram}
          onClose={() => {
            setSelectedProgram(null);
            setTimeout(() => { if ((cartItems?.length || 0) > preAddCartCount && availableAddons.length > 0) setShowAddonsOffer(true); }, 200);
          }}
          onComplete={() => setSelectedProgram(null)}
          brandingHint={{ primary_color: theme.primary, accent_color: theme.accent }} brand={brand} storeSlug={storeSlug} mealItems={items}
        />
      )}
      {selectedPlan && (
        <AddToCartModal
          subdomain={subdomain} mealPlan={selectedPlan} storeSlug={storeSlug} storeId={selectedPlan?.store_ids?.[0] || null}
          brandingHint={{ primary_color: theme.primary, accent_color: theme.accent }} availableAddons={availableAddons}
          onCheckout={() => { setSelectedPlan(null); navigate(checkoutDest()); }} onClose={() => setSelectedPlan(null)}
          brand={brand} photo={planPhotoOf(plans, selectedPlan)}
        />
      )}
      {selectedPackage && (
        <CateringOrderModal
          package={selectedPackage} availability={catering.availability || undefined} brandPrimary={theme.primary} brand={brand}
          itemsById={(() => { const m = {}; items.forEach((i) => { m[i.id] = i; }); Object.entries(catering.dishImages || {}).forEach(([id, img]) => { if (!m[id]) m[id] = img; else if (!m[id].image_url && img.image_url) m[id] = { ...m[id], ...img }; }); return m; })()}
          onClose={() => setSelectedPackage(null)}
          onSubmit={(options) => {
            addCateringToCart(selectedPackage, { ...options, storeSlug: storeSlug || '', storeId: selectedPackage?.store_ids?.[0] || null, storeName: tenantMeta?.name || '' });
            setSelectedPackage(null);
            navigate(checkoutDest());
          }}
        />
      )}
      {showAddonsOffer && (
        <AddonsOfferModal
          open={showAddonsOffer} addons={availableAddons} onClose={() => setShowAddonsOffer(false)}
          onSelectAddon={(addon) => { setShowAddonsOffer(false); setAddonSubjectPlan(addon); }}
          onContinueToCheckout={() => { setShowAddonsOffer(false); navigate(checkoutDest()); }}
          brandingHint={{ primary_color: theme.primary, accent_color: theme.accent }} brand={brand}
        />
      )}
      {addonSubjectPlan && (
        <AddToCartModal
          subdomain={subdomain} mealPlan={addonSubjectPlan} storeSlug={storeSlug} storeId={addonSubjectPlan?.store_ids?.[0] || null}
          brandingHint={{ primary_color: theme.primary, accent_color: theme.accent }} availableAddons={[]}
          onCheckout={() => { setAddonSubjectPlan(null); navigate(checkoutDest()); }}
          onClose={() => { setAddonSubjectPlan(null); setShowAddonsOffer(true); }}
          brand={brand} photo={planPhotoOf(plans, addonSubjectPlan)}
        />
      )}

      {isAuthed && !isPreview && (
        <OpsyCustomerChat storeId={storeSlug} />
      )}
      {!isAuthed && !isPreview && (
        <SupportChatWidget subdomain={subdomain} storeId={storeSlug || null} mode="openChat" agentName={opsyName || 'OpSy'} />
      )}
    </div>
    </StorefrontEntrance>
  );
}
